const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const R2_DOMAIN = "pub-751830794bf1401f933c0ea71fb0430e.r2.dev";
const R2_PRIVATE_SUFFIX = ".r2.cloudflarestorage.com";
const INTERNAL_MEDIA_BASE = `${SUPABASE_URL.replace(/\/+$/, "")}/internal-media/`;
const PUBLIC_R2_PREFIXES = [
  "catalog/",
  "b2c-banners/",
  "b2c-branding/",
  "b2c-products/",
  "b2c-tryon-results/",
  "today/",
];

function isPublicR2Path(path: string) {
  return PUBLIC_R2_PREFIXES.some((prefix) => path.includes(prefix));
}

export function normalizeR2Url(url: string | null | undefined): string {
  if (!url) return "";
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return "";

  let absoluteUrl = trimmedUrl;
  if (!/^https?:\/\//i.test(trimmedUrl)) {
    if (/^(data:|blob:|file:|\/)/i.test(trimmedUrl)) return trimmedUrl;
    if (trimmedUrl.startsWith(`${R2_DOMAIN}/`)) {
      absoluteUrl = `https://${trimmedUrl}`;
    } else {
      absoluteUrl = `https://${R2_DOMAIN}/${trimmedUrl.replace(/^\/+/, "")}`;
    }
  }

  try {
    const parsed = new URL(absoluteUrl);

    // Rewrite R2 domains to VPS internal-media base. This ensures that assets
    // migrated from R2 to the self-hosted VPS continue to load correctly even
    // if the database records still point to the old Cloudflare R2 bucket.
    if (parsed.hostname === R2_DOMAIN || parsed.hostname.endsWith(R2_PRIVATE_SUFFIX)) {
      const path = parsed.pathname.replace(/^\/+/, "");
      if (isPublicR2Path(path)) return parsed.toString();
      return `${INTERNAL_MEDIA_BASE}${path}`;
    }

    return parsed.toString();
  } catch {
    return absoluteUrl;
  }
}

export function proxyR2Url(url: string | null | undefined, w?: number): string {
  const normalizedUrl = normalizeR2Url(url);
  if (!normalizedUrl) return "";
  const isProxyable =
    normalizedUrl.includes(R2_DOMAIN) || normalizedUrl.startsWith(INTERNAL_MEDIA_BASE);
  if (!isProxyable) return normalizedUrl;
  const base = `${SUPABASE_URL}/functions/v1/image-proxy?url=${encodeURIComponent(normalizedUrl)}`;
  return w ? `${base}&w=${w}` : base;
}

export function resolveR2DownloadUrl(url: string | null | undefined): string {
  const normalized = normalizeR2Url(url);
  if (!normalized) return "";
  // VPS internal-media paths are auth-gated — route through proxy so the browser
  // can open/download them without needing a session cookie.
  if (normalized.startsWith(INTERNAL_MEDIA_BASE)) return proxyR2Url(url);
  return normalized;
}

// For publicly shared pages (portal dispatch, catalog, shop) where the viewer
// has NO Supabase session. Returns the normalized URL directly — nginx serves
// /internal-media/ publicly so no proxy is needed.
export function publicR2Url(url: string | null | undefined): string {
  return normalizeR2Url(url);
}

// b2c-products images have pre-generated size siblings in R2 with derivable
// names: <name>.webp (main) + <name>-thumb.webp (480px) + <name>-med.webp
// (1080px). Created by uploadImageWithDerivedSizes (r2Upload.ts) and
// scripts/backfill-b2c-image-sizes.mjs. The image-proxy edge fn does NOT
// resize (Bug #85), so these stored copies are the only real byte reduction.
const B2C_SIZED_SEGMENT = "/b2c-products/";
const B2C_THUMB_MAX_W = 480;

function deriveB2CSizedUrl(normalized: string, w: number): string | null {
  try {
    const parsed = new URL(normalized);
    if (parsed.hostname !== R2_DOMAIN) return null;
    if (!parsed.pathname.includes(B2C_SIZED_SEGMENT)) return null;
    const dot = parsed.pathname.lastIndexOf(".");
    if (dot < 0) return null;
    const base = parsed.pathname.slice(0, dot);
    if (base.endsWith("-thumb") || base.endsWith("-med")) return null;
    parsed.pathname = `${base}${w <= B2C_THUMB_MAX_W ? "-thumb" : "-med"}.webp`;
    return parsed.toString();
  } catch {
    return null;
  }
}

// For <img> display — routes through the resize edge function when a width is
// given (thumbnails, card images) so the browser receives a resized WebP with a
// 1-year immutable cache. VPS internal-media URLs are ALWAYS proxied even without
// a width because the raw /internal-media/ path requires server-side auth and
// cannot be loaded directly by the browser.
// b2c-products URLs short-circuit to their stored -thumb/-med sibling instead
// (callers must keep an onError fallback to the unsized URL for images whose
// siblings don't exist yet).
export function displayR2Url(url: string | null | undefined, w?: number): string {
  const normalized = normalizeR2Url(url);
  if (!normalized) return "";
  // VPS internal-media URLs must always go through the proxy (auth-gated path)
  const isInternalMedia = normalized.startsWith(INTERNAL_MEDIA_BASE);
  if (w && !isInternalMedia) {
    const sized = deriveB2CSizedUrl(normalized, w);
    if (sized) return sized;
  }
  if (w || isInternalMedia) return proxyR2Url(url, w);
  return normalized;
}
